import React, { useMemo } from 'react'

import useLegalAgreements, { GuardedScope } from '../hooks/useLegalAgreements'
import { CheckoutNavigationBarOptionsContext } from '../layouts/context'
import { useAuth } from '../lib/auth'
import { useDispensaryContext } from '../lib/dispensaries'
import { useOnboardPatientClaim } from '../lib/onboarding/hooks/useOnboardPatientClaim'

const CheckoutNavigationBarOptionsProvider: React.FC = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()
  const { requiredForScope } = useLegalAgreements()
  const { isOnboardingComplete } = useOnboardPatientClaim()
  const dispensary = useDispensaryContext()

  const requiredForCheckout = useMemo(
    () => requiredForScope(GuardedScope.CHECKOUT),
    [requiredForScope],
  )

  const displayAgreements = useMemo(
    () => isAuthenticated && !isLoading && requiredForCheckout.length > 0,
    [isAuthenticated, isLoading, requiredForCheckout.length],
  )

  const displayRegistration = useMemo(
    () => !isAuthenticated && !isLoading && !!dispensary,
    [isAuthenticated, isLoading, dispensary],
  )

  // Since the patient can complete onboarding via checking out with a dispensary,
  // don't display the setup step if the patient is currently on a dispensary
  const displayOnboarding = !isOnboardingComplete && !dispensary

  const navigationBarOptions = useMemo(
    () =>
      [
        ...(displayRegistration ? ['registration'] : []),
        ...(displayAgreements ? ['agreements'] : []),
        ...(displayOnboarding ? ['setup'] : []),
        'shipping',
        'delivery',
        'payment',
        'review',
      ].map((label, index) => ({ label, value: index })),
    [displayRegistration, displayAgreements, displayOnboarding],
  )

  return (
    <CheckoutNavigationBarOptionsContext.Provider
      value={{
        navigationBarOptions,
        displayAgreements,
        displayRegistration,
        displayOnboarding,
        loading: isLoading,
      }}
    >
      {children}
    </CheckoutNavigationBarOptionsContext.Provider>
  )
}

export default CheckoutNavigationBarOptionsProvider
